/**
 * Загрузка словаря частей речи для проверки на канцелярит.
 *
 * Словарь весит мегабайт, поэтому он не едет вместе со страницей. Проверка
 * штампов, ритма и строения работает сразу и без него; словарь нужен
 * одному пункту из восьми — отношению существительных к глаголам.
 *
 * Грузим по нажатию, и человека об этом предупреждаем прямо на кнопке.
 * Скачанный файл остаётся в кэше браузера, так что платит он один раз, а
 * не при каждой проверке. В памяти вкладки держим разобранный словарь:
 * разбор двух миллионов слов занимает заметное время, и повторять его на
 * каждую проверку было бы обиднее, чем саму загрузку.
 *
 * Источник: OpenCorpora (opencorpora.org), лицензия CC BY-SA. Файл
 * собирает scripts/sobrat-slovar-morfologii.py.
 */

import type { SlovarChastejRechi } from './proverka';

/** Адрес словаря. Отдаётся статикой, ходить за ним на наш API незачем. */
export const PUT_SLOVARYA = '/slovar/chasti-rechi.txt.gz';

/** Вес файла для подписи на кнопке: человек должен знать, на что жмёт. */
export const VES_SLOVARYA_MB = 1;

let zagruzhennyj: SlovarChastejRechi | null = null;
let vRabote: Promise<SlovarChastejRechi> | null = null;

/**
 * Разворачивает список, сжатый общим началом.
 *
 * Формат: первый знак строки говорит, сколько букв взять от предыдущего
 * слова, остальное дописывается. «решение», затем «5м» даёт «решением».
 * Так список из двух миллионов слов занимает мегабайт вместо шестидесяти.
 */
function razvernut(blok: string): Set<string> {
  const out = new Set<string>();
  let prev = '';
  for (const stroka of blok.split('\n')) {
    if (!stroka) continue;
    const obshee = stroka.charCodeAt(0) - 48;
    const slovo = prev.slice(0, obshee) + stroka.slice(1);
    out.add(slovo);
    prev = slovo;
  }
  return out;
}

/** Уже в памяти? Нужно интерфейсу, чтобы не предлагать загрузку повторно. */
export function slovarGotov(): SlovarChastejRechi | null {
  return zagruzhennyj;
}

export async function zagruzitSlovar(): Promise<SlovarChastejRechi> {
  if (zagruzhennyj) return zagruzhennyj;
  // Второе нажатие во время загрузки не должно тянуть файл заново.
  if (vRabote) return vRabote;

  vRabote = (async () => {
    const otvet = await fetch(PUT_SLOVARYA);
    if (!otvet.ok) throw new Error(`словарь не загрузился: ${String(otvet.status)}`);

    // Сервер отдаёт файл с Content-Encoding: gzip, и браузер распаковывает
    // его сам. Но если он придёт как есть (например, со статики без этого
    // заголовка), внутри окажется gzip-поток, и текст начнётся с двух
    // байтов 1f 8b. Проверяем и распаковываем сами, иначе пользователь
    // увидел бы пустой словарь без единой ошибки в консоли.
    const buf = await otvet.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let text: string;
    if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
      const telo = new Response(buf).body;
      // Тело у только что собранного ответа есть всегда, но типы допускают null.
      if (!telo) throw new Error('словарь не распаковался');
      text = await new Response(telo.pipeThrough(new DecompressionStream('gzip'))).text();
    } else {
      text = new TextDecoder().decode(buf);
    }

    const [sush, glag] = text.split('\n\n');
    // Пустая строка — разделитель двух списков, и без неё файл битый.
    // Молча собрать словарь из одной половины нельзя: проверка тогда
    // сочла бы все глаголы неизвестными словами и обвинила бы в
    // канцелярите любой текст.
    if (sush === undefined || glag === undefined) throw new Error('словарь повреждён');

    zagruzhennyj = {
      sushestvitelnye: razvernut(sush),
      glagolnye: razvernut(glag),
    };
    return zagruzhennyj;
  })();

  try {
    return await vRabote;
  } finally {
    // Сбрасываем в любом случае: после неудачи следующее нажатие должно
    // пробовать заново, а не возвращать отвергнутое обещание навсегда.
    vRabote = null;
  }
}
